import Template from "ui/Template";
import Canvas, {EVENT_HISTORY_CHANGED, EVENT_SAVED} from 'ui/canvas/Canvas';
import {DISPLAY_MODE_EDIT, DISPLAY_MODE_VIEW, EVENT_PROCESS_START, EVENT_PROCESS_FINISH} from 'ui/canvas/Canvas';
import ColorsSet from 'ui/editor/toolboxes/colors-set/ColorsSet';
import Tools from 'ui/editor/toolboxes/tools/Tools';
import TimelineLayers from 'ui/editor/toolboxes/timeline-layers/TimelineLayers';
import PaletteModel from "ui/canvas/models/PaletteModel";
import DisplayModes from "ui/editor/toolboxes/display-modes/DisplayModes";
import ColorsSetModel from "ui/canvas/models/ColorsSetModel";
import Brush from "ui/editor/toolboxes/brush/Brush";
import ScenePreview from "ui/scene/ScenePreview";
import PageElement from "ui/PageElement";
import SceneEditorTopPanel from "./SceneEditorTopPanel";
import {i18n} from "ui/i18n";
import {PROJECT_NAME, SCENE_FRAMES, SCENE_SOURCE_HEIGHT, SCENE_SOURCE_WIDTH} from "ui/config";
import SceneSprites from "ui/scene/SceneSprites";
import LayerTools from "ui/editor/toolboxes/layer-tools/LayerTools";
import ColorPicker from "ui/editor/toolboxes/color-picker/ColorPicker";
import editorView from './editor.handlebars';
import Spinner from "ui/components/spinner/Spinner";
import SceneEditorStorage from "ui/editor/scene-editor/SceneEditorStorage";
import {CurrentUser, ROLES} from "ui/CurrentUser";
import UnsupportedBrowserDetector from "ui/editor/scene-editor/UnsupportedBrowserDetector";
import AnglePicker from "ui/editor/toolboxes/angle-picker/AnglePicker";
import Guide from "ui/editor/dialogs/guide/Guide";
import {SceneEditorVideoRecorder} from "./SceneEditorVideoRecorder";

/**
 * Scene Editor Canvas
 */
export default class SceneEditor extends PageElement {
	/**
	 * Class constructor
	 *
	 * @param {T_SceneEditorOptions} options
	 */
	constructor(options = {}) {
		super(options);

		/**
		 * @type {boolean}
		 * @private
		 */
		this._isElement = /^l\d+/.test(options.sceneId);

		const user = CurrentUser.get();

		this._element = new Template(editorView)
			.createElement({
				isUser: user.isUser(),
				isModerator: user.isModerator(),
				userName: user.userName,
				userRole: ROLES[user.userRole],
				scenesListURL: '/desktop' + (this._isElement ? '#elements' : '#scenes'),
				addSceneURL: '/editor/l0',
			});

		/**
		 * @type {string}
		 * @private
		 */
		this._sceneId = options.sceneId;

		/**
		 * @type {T_AreaSize}
		 * @private
		 */
		this._screenSize = {
			width: 0,
			height: 0
		};

		/**
		 * @type {T_AreaSize}
		 * @private
		 */
		this._sceneSize = {
			width: SCENE_SOURCE_WIDTH,
			height: SCENE_SOURCE_HEIGHT,
		};

		this._els = {
			topPanel: this._element.querySelector('.editor-container__top-panel'),
			canvasCont: this._element.querySelector('.editor-container__canvas'),
			toolsCont: this._element.querySelector('.editor-container__tools'),
			dragZoneRight: this._element.querySelector('.editor-container__drag-zone_right'),
			dragZoneLeft: this._element.querySelector('.editor-container__drag-zone_left'),
			cover: this._element.querySelector('.editor-container__cover'),
		};

		/**
		 * @type {PaletteModel}
		 * @private
		 */
		this._paletteModel = new PaletteModel();

		/**
		 * @type {ColorsSetModel}
		 * @private
		 */
		this._colorsSetModel = new ColorsSetModel();

		/**
		 * @type {Canvas}
		 * @private
		 */
		this._canvas = new Canvas({
			sceneWidth: this._sceneSize.width,
			sceneHeight: this._sceneSize.height,
			palette: this._paletteModel,
			colorsSet: this._colorsSetModel,
			parent: this._els.canvasCont,
			frames: SCENE_FRAMES
		});

		/**
		 * @type {SceneEditorVideoRecorder}
		 * @private
		 */
		this._videoRecorder = new SceneEditorVideoRecorder(this._canvas);

		/**
		 * @type {SceneEditorTopPanel}
		 * @private
		 */
		this._topPanel = new SceneEditorTopPanel(this, this._els.topPanel);

		this._colorPicker = new ColorPicker(this._paletteModel, {
			scene: this,
			isDraggable: true,
			isCollapsable: true,
			isPinnedAtRight: true,
			parent: this._els.toolsCont,
		});

		/**
		 * @type {ColorsSet}
		 * @private
		 */
		this._colorsSets = new ColorsSet({
			scene: this,
			colorsSet: this._colorsSetModel,
			isDraggable: true,
			isCollapsable: true,
			isPinnedAtRight: true,
			parent: this._els.toolsCont
		});

		/**
		 * @type {LayerTools}
		 * @private
		 */
		this._layerTools = new LayerTools({
			scene: this,
			colorsSet: this._colorsSetModel,
			isDraggable: true,
			isCollapsable: true,
			isPinnedAtRight: true,
			parent: this._els.toolsCont
		});

		/**
		 * @type {AnglePicker}
		 * @private
		 */
		this._anglePicker = new AnglePicker({
			scene: this,
			isDraggable: true,
			isCollapsable: true,
			isPinnedAtRight: true,
			parent: this._els.toolsCont
		});

		/**
		 * @type {Tools}
		 * @private
		 */
		this._tools = new Tools({
			scene: this,
			isDraggable: true,
			isCollapsable: false,
			isPinnedAtLeft: true,
			parent: this._els.toolsCont
		});

		/**
		 * @type {DisplayModes}
		 * @private
		 */
		this._displayMode = new DisplayModes({
			scene: this,
			isDraggable: true,
			isCollapsable: true,
			isPinnedAtLeft: true,
			parent: this._els.toolsCont
		});

		/**
		 * @type {Brush}
		 * @private
		 */
		this._brush = new Brush({
			scene: this,
			isDraggable: true,
			isCollapsable: true,
			isPinnedAtLeft: true,
			parent: this._els.toolsCont
		});

		/**
		 * @type {TimelineLayers}
		 * @private
		 */
		this._timelineLayers = new TimelineLayers(this, {
			scene: this,
			isDraggable: false,
			isCollapsable: true,
			isPinnedAtBottom: true,
			isResizeTop: true,
			height: 200,
			parent: this._els.toolsCont
		});

		this._handlers = {
			keyDown: this._onKeyDown.bind(this),
			keyUp: this._onKeyUp.bind(this),
			windowResize: this._onWindowResize.bind(this),
			windowUnload: this._onWindowUnload.bind(this),
			updateSize: this._updateSize.bind(this),
			updateTitle: this._updateTitle.bind(this),
		};

		/**
		 * @type {SceneEditorStorage}
		 * @private
		 */
		this._storage = new SceneEditorStorage(this);

		/**
		 * @type {{}}
		 * @private
		 */
		this._pressedKeys = {};

		/**
		 * @type {string}
		 * @private
		 */
		this._lastTool = '';

		/**
		 * @type {{resizeTimer: number}}
		 * @private
		 */
		this._timers = {
			resizeTimer: 0
		};

		this._anglePicker.toggle(false);
		this.activatePageElement();

		this._listenEvents();
		this._updateSize();

		setTimeout(async () => {
			this._storage.load().then();
			this._updateSize();
			this.rearrangeToolbars();
			this._timelineLayers.render();
			if (!(await new UnsupportedBrowserDetector().checkAndAlert())) {
				window.location.href = '/';
			} else if (window.location.hash === '#guide') {
				new Guide();
			}
		}, 0);
	}

	/**
	 * @returns {Canvas}
	 */
	get canvas() {
		return this._canvas;
	}

	/**
	 * @returns {SceneEditorVideoRecorder}
	 */
	get videoRecorder() {
		return this._videoRecorder;
	}

	/**
	 * @returns {SceneEditorStorage}
	 */
	get storage() {
		return this._storage;
	}

	/**
	 * @return {T_AreaSize}
	 */
	get screenSize() {
		return this._screenSize;
	}

	/**
	 * @param {string} id
	 */
	set sceneId(id) {
		if (this._sceneId === id) {
			return;
		}

		this._sceneId = id;
		window.history.pushState({}, document.title, '/editor/' + id);
	}

	/**
	 * @return {string}
	 */
	get sceneId() {
		return this._sceneId;
	}

	/**
	 * @param {T_CanvasData} data
	 */
	async setSceneData(data) {
		await this._canvas.setData(data);
		this._canvas.render();
		this._timelineLayers.render();
		this._tools.updateSelectedTool();
		this._updateSize();
		this.rearrangeToolbars();
		Spinner.hide();
	}

	/**
	 * Shows/hides right Toolbar Drag Zone
	 *
	 * @param {boolean} flag
	 * @returns {SceneEditor}
	 */
	toggleRightToolbarDragZone(flag) {
		this._els.dragZoneRight.classList.toggle('editor-container__drag-zone_show', flag === true);
		return this;
	}

	/**
	 * Shows/hides left Toolbar Drag Zone
	 *
	 * @param {boolean} flag
	 * @returns {SceneEditor}
	 */
	toggleLeftToolbarDragZone(flag) {
		this._els.dragZoneLeft.classList.toggle('editor-container__drag-zone_show', flag === true);
		return this;
	}

	/**
	 * Rearrange all toolboxes at right and left toolbars
	 *
	 * @returns {SceneEditor}
	 */
	rearrangeToolbars() {
		for (let side of ['left', 'right']) {
			let els = this._element.querySelectorAll('.editor-toolbox_pin-' + side),
				list = [];
			els.forEach((el) => {
				list.push({el, rect: el.getBoundingClientRect()});
			});
			list.sort((a, b) => a.rect.top - b.rect.top);
			let y = 0;
			for (let item of list) {
				item.el.style.top = y + 'px';
				y += item.rect.height;
			}
		}
		return this;
	}

	/**
	 * Shows/hides tool bars
	 *
	 * @returns {SceneEditor}
	 */
	toggleTools() {
		this._els.toolsCont.classList.toggle('editor-container__tools_hide');
		this._topPanel.update();
		return this;
	}

	/**
	 * Returns TRUE if editor tools presently are visible
	 *
	 * @returns {boolean}
	 */
	isToolsVisible() {
		return !this._els.toolsCont.classList.contains('editor-container__tools_hide');
	}

	/**
	 * Shows scene animation preview screen
	 *
	 * @param {boolean} magnified
	 * @returns {Promise<void>}
	 */
	async showPreview(magnified = false) {
		let sprites = new SceneSprites();
		Spinner.show();
		await sprites.generateForCanvas(this._canvas);
		Spinner.hide();
		new ScenePreview(
			sprites,
			magnified ? null : this._canvas.neighborScenes,
			magnified ? 2 : 1
		).show();
	}

	/**
	 * Adds events listeners
	 *
	 * @private
	 * @returns {void}
	 */
	_listenEvents() {
		document.addEventListener('keydown', this._handlers.keyDown);
		document.addEventListener('keyup', this._handlers.keyUp);
		window.addEventListener('blur', this._cancelAllPressedKeys.bind(this));
		window.addEventListener('resize', this._handlers.windowResize);
		window.addEventListener('beforeunload', this._handlers.windowUnload);

		this._canvas.events.addListener(EVENT_PROCESS_START, Spinner.show);
		this._canvas.events.addListener(EVENT_PROCESS_FINISH, Spinner.hide);
		this._canvas.events.addListener(EVENT_HISTORY_CHANGED, this._handlers.updateTitle);
		this._canvas.events.addListener(EVENT_SAVED, this._handlers.updateTitle);
	}

	/**
	 * Handles window close/unload
	 *
	 * @private
	 * @param {Event} e
	 * @returns {?string}
	 */
	_onWindowUnload(e) {
		if (this._canvas.isStateSaved()) {
			return null;
		}

		let msg = i18n('messages.not-saved');
		// noinspection JSValidateTypes
		e.returnValue = msg;
		return msg;
	}

	/**
	 * Handles window resize
	 *
	 * @private
	 * @returns {void}
	 */
	_onWindowResize() {
		if (this._timers.resizeTimer > 0) {
			clearTimeout(this._timers.resizeTimer);
		}
		this._timers.resizeTimer = setTimeout(this._handlers.updateSize, 100);
	}

	/**
	 * Updates editor size
	 *
	 * @private
	 * @returns {void}
	 */
	_updateSize() {
		let width = window.innerWidth
			|| document.documentElement.clientWidth
			|| document.body.clientWidth,
			height = window.innerHeight
			|| document.documentElement.clientHeight
			|| document.body.clientHeight;
		this._screenSize.width = width;
		this._screenSize.height = height;
		this._canvas.setSize(width, height);
		this._canvas.render();
	}

	/**
	 * @private
	 * @returns {void}
	 */
	_updateTitle() {
		document.title = PROJECT_NAME + (this._canvas.isStateSaved() ? '' : ' [Unsaved]');
	}

	/**
	 * Handles key down event
	 *
	 * @param {KeyboardEvent} e
	 * @returns {void}
	 * @private
	 */
	_onKeyDown(e) {
		if (!this._isSelfFocus()) {
			// the editor not in focus
			return;
		}
		let canvas = this._canvas;
		this._pressedKeys[e.code] = 1;

		switch (e.code) {
			case 'BracketRight':
				canvas.setBrush(canvas.getBrushSize() + 1);
				break;
			case 'BracketLeft':
				canvas.setBrush(canvas.getBrushSize() - 1);
				break;
			case 'KeyA':
				if (this._pressedKeys['Control']) {
					e.preventDefault();
					canvas.selectAll();
				}
				break;
			case 'KeyS':
				if (this._pressedKeys['Control']) {
					e.preventDefault();
					this._storage.save().then();
				}
				break;
			case 'KeyC':
				if (this._pressedKeys['Control']) {
					e.preventDefault();
					if (canvas.selection.hasSelection) {
						canvas.copySelectedFragment();
					} else {
						canvas.copyFrames();
					}
				}
				break;
			case 'KeyV':
				if (this._pressedKeys['Control']) {
					e.preventDefault();
					canvas.paste(this._pressedKeys['Shift'] === 1);
				} else {
					if (canvas.getDisplayMode() === DISPLAY_MODE_EDIT) {
						canvas.setDisplayMode(DISPLAY_MODE_VIEW);
					} else {
						canvas.setDisplayMode(DISPLAY_MODE_EDIT);
					}
				}
				break;
			case 'KeyX':
				if (this._pressedKeys['Control']) {
					e.preventDefault();
					canvas.copySelectedFragment(true);
				}
				break;
			case 'KeyB':
				e.preventDefault();
				canvas.setTool('brush');
				break;
			case 'KeyJ':
				e.preventDefault();
				canvas.setTool('brush_bg');
				break;
			case 'KeyE':
				e.preventDefault();
				canvas.setTool('erase');
				break;
			case 'KeyH':
				e.preventDefault();
				canvas.setTool('move');
				break;
			case 'KeyL':
				e.preventDefault();
				canvas.setTool('lasso');
				break;
			case 'KeyP':
				e.preventDefault();
				canvas.setTool('poly_lasso');
				break;
			case 'KeyR':
				if (!this._pressedKeys['Control']) {
					e.preventDefault();
					canvas.setTool('rect');
				}
				break;
			case 'KeyO':
				e.preventDefault();
				canvas.setTool('ellipse');
				break;
			case 'KeyI':
				e.preventDefault();
				canvas.setTool('picker');
				break;
			case 'KeyK':
				e.preventDefault();
				canvas.setTool('fill');
				break;
			case 'KeyT':
				e.preventDefault();
				canvas.setTool('translate');
				break;
			case 'KeyZ':
				if (this._pressedKeys['Control']) {
					if (this._pressedKeys['Shift']) {
						canvas.redo();
					} else {
						canvas.undo();
					}
				} else {
					canvas.setTool('zoom');
				}
				break;
			case 'KeyG':
				canvas.enableGrid(!canvas.isGridEnabled());
				break;
			case 'ArrowRight':
			case 'Period':
				{
					let index = this._canvas.getCurrentFrame();
					if (index < this._canvas.getTotalFrames()) {
						this._canvas.setFrame(index + 1);
					}
				}
				break;
			case 'ArrowLeft':
			case 'Comma':
				{
					let index = this._canvas.getCurrentFrame();
					if (index > 0) {
						this._canvas.setFrame(index - 1);
					}
				}
				break;
			case 'ArrowUp':
				{

					const layers = this._canvas.getLayers();
					const prevLayer = layers[layers.indexOf(this._canvas.getCurrentLayer()) - 1];
					if (prevLayer) {
						this._canvas.selectLayer(prevLayer);
					}
				}
				break;
			case 'ArrowDown':
				{

					const layers = this._canvas.getLayers();
					const nextLayer = layers[layers.indexOf(this._canvas.getCurrentLayer()) + 1];
					if (nextLayer) {
						this._canvas.selectLayer(nextLayer);
					}
				}
				break;
			case 'Delete':
				canvas.eraseSelectedArea();
				break;
			case 'Equal':
			case 'NumpadAdd':
				if (this._pressedKeys['Control']) {
					e.preventDefault();
				}
				canvas.setScale(canvas.getScale() + 1);
				break;
			case 'Minus':
			case 'NumpadSubtract':
				if (this._pressedKeys['Control']) {
					e.preventDefault();
				}
				canvas.setScale(canvas.getScale() - 1);
				break;
			case 'Digit0':
				if (this._pressedKeys['Control']) {
					e.preventDefault();
				}
				canvas.setScale(1);
				break;
			case 'Space':
				e.preventDefault();
				let tool = canvas.getTool();
				if (tool !== 'move') {
					this._lastTool = canvas.getTool();
					canvas.setTool('move');
				}
				break;
			case 'Shift':
			case 'ShiftLeft':
			case 'ShiftRight':
				this._pressedKeys['Shift'] = 1;
				break;
			case 'MetaRight':
			case 'MetaLeft':
			case 'OSRight':
			case 'OSLeft':
			case 'Control':
			case 'ControlLeft':
			case 'ControlRight':
				this._pressedKeys['Control'] = 1;
				break;
			case 'Esc':
			case 'Escape':
				canvas.dropSelection();
				break;
			case 'Tab':
				e.preventDefault();
				this.toggleTools();
				break;
			case 'Alt':
			case 'AltLeft':
			case 'AltRight':
				this._pressedKeys['Alt'] = 1;
				break;
			case 'F5':
				e.preventDefault();
				canvas.insertFrame();
				break;
			case 'F6':
				e.preventDefault();
				canvas.makeKeyFrame();
				break;
			case 'F7':
				e.preventDefault();
				canvas.makeBlankKeyFrame(this._pressedKeys['Shift']);
				break;
			case 'F8':
				e.preventDefault();
				canvas.removeFrame();
				break;
			case 'F9':
				e.preventDefault();
				void this.showPreview(this._pressedKeys['Shift']);
				break;
			case 'F10':
				e.preventDefault();
				void this._storage.save();
				break;
			case 'Digit1':
			case 'Digit2':
			case 'Digit3':
			case 'Digit4':
			case 'Digit5':
				this._colorsSetModel.switchOpacitySlot(parseInt(e.code.match(/\d+$/)[0]) - 1);
				break;
			case 'KeyU':
				if (this._pressedKeys['Shift'] && this._pressedKeys['Control']) {
					this._canvas.testNoiseFill();
				}
				break;
		}
		this._tools.updateSelectedTool();
		this._displayMode.updateSelectedTool();
		this._displayMode.updateFlags();
	}

	/**
	 * Handles key up event
	 *
	 * @param {KeyboardEvent} e
	 * @returns {void}
	 * @private
	 */
	_onKeyUp(e) {
		delete this._pressedKeys[e.code];
		switch (e.code) {
			case 'Space':
				this._canvas.setTool(this._lastTool);
				break;
			case 'Shift':
			case 'ShiftLeft':
			case 'ShiftRight':
				delete this._pressedKeys['Shift'];
				break;
			case 'Control':
			case 'ControlLeft':
			case 'ControlRight':
				delete this._pressedKeys['Control'];
				break;
			case 'Alt':
			case 'AltLeft':
			case 'AltRight':
				delete this._pressedKeys['Alt'];
				break;
		}
		this._tools.updateSelectedTool();
	}

	/**
	 * @private
	 */
	_cancelAllPressedKeys() {
		this._pressedKeys = {};
		this._tools.updateSelectedTool();
	}
}
